import { useRef, useState } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { TypeAnimation } from 'react-type-animation'
import { FaLinkedin, FaGithub, FaDribbble } from 'react-icons/fa'
import { HiDownload } from 'react-icons/hi'
import type { Language } from '../App'
import { t } from '../utils/translations'
import { personalInfo } from '../utils/data'
import './About.css'

interface AboutProps { language: Language }

export default function About({ language }: AboutProps) {
  const ref = useRef<HTMLElement>(null)
    const [imgError, setImgError] = useState(false)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%'])
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0])

  const socialLinks = [
    { icon: <FaLinkedin />, label: 'LinkedIn',  href: personalInfo.linkedin,  color: '#0A66C2' },
    { icon: <FaGithub />,   label: 'GitHub',    href: personalInfo.github,    color: '#fff' },
    { icon: <FaDribbble />, label: 'Dribbble',  href: personalInfo.dribbble,  color: '#EA4C89' },
  ]

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
  }

  const itemVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1] } },
  }

  return (
    <section id="about" ref={ref} className="about-section">
      {/* Animated background orbs */}
      <div className="orb orb-purple about-orb-1" />
      <div className="orb orb-pink about-orb-2" /> 
      <div className="orb orb-cyan about-orb-3" />

      {/* Floating particles */}
      {[...Array(6)].map((_, i) => (
        <div key={i} className="particle" style={{ '--i': i } as React.CSSProperties} />
      ))}

      <motion.div className="container about-container" style={{ y, opacity }}>
        <motion.div
          className="about-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header greeting */}
          <motion.div className="about-greeting" variants={itemVariants}>
            <div className="greeting-badge">
              <span className="greeting-dot" />
              {t('about.greeting', language)}
            </div>
            <p className="greeting-sub">{t('about.intro', language)}</p>
          </motion.div>

          <div className="about-main">
            {/* Avatar */}
            <motion.div
              className="about-avatar-wrapper"
              variants={itemVariants}
            >
              <div className="avatar-ring animate-spin-slow" />
              <div className="avatar-container animate-float">
                {!imgError ? (
                  <img
                    src={personalInfo.headshot}
                    alt={personalInfo.name}
                    className="avatar-photo"
                    onError={() => setImgError(true)}
                    draggable={false}
                  />
                ) : (
                  <div className="avatar-placeholder">
                    <span>KB</span>
                  </div>
                )}
                <div className="avatar-glow animate-glow" />
              </div>
              {/* Floating badges */}
              <motion.div
                className="float-badge float-badge-1"
                animate={{ y: [-5, 5, -5] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              >
                💻 4+ Years
              </motion.div>
              <motion.div
                className="float-badge float-badge-2"
                animate={{ y: [5, -5, 5] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
              >
                🚀 Full Stack
              </motion.div>
            </motion.div>

            {/* Text content */}
            <motion.div className="about-text" variants={itemVariants}>
              <div className="intro-line">
                <span className="intro-iam">{t('about.iam', language)}</span>
                <span className="intro-name">{personalInfo.name}.</span>
              </div>

              <div className="intro-role">
                <TypeAnimation
                  sequence={[
                    'A Software Engineer',
                    2000,
                    'An AI Enthusiast',
                    2000,
                    'An Innovator',
                    2000,
                    'A Problem Solver',
                    2000,
                  ]}
                  wrapper="span"
                  speed={50}
                  repeat={Infinity}
                  className="type-text"
                />
              </div>

              <p className="intro-years">{t('about.years', language)}</p>

              {/* Social links */}
              <motion.div className="social-links" variants={itemVariants}>
                {socialLinks.map((link, i) => (
                  <motion.a
                    key={i}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-btn"
                    whileHover={{ scale: 1.05, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    style={{ '--social-color': link.color } as React.CSSProperties}
                  >
                    <span className="social-icon">{link.icon}</span>
                    <span>{link.label}</span>
                  </motion.a>
                ))}
              </motion.div>

              {/* Resume buttons */}
              <motion.div className="resume-buttons" variants={itemVariants}>
                <motion.a
                  href={personalInfo.resumeEN}
                  download
                  className="btn btn-secondary resume-btn"
                  whileHover={{ scale: 1.04, y: -2 }}
                  whileTap={{ scale: 0.96 }}
                >
                  <HiDownload />
                  {t('about.resumeEN', language)}
                </motion.a>
                <div className="resume-divider" />
                <motion.a
                  href={personalInfo.resumeDE}
                  download
                  className="btn btn-secondary resume-btn"
                  whileHover={{ scale: 1.04, y: -2 }}
                  whileTap={{ scale: 0.96 }}
                >
                  <HiDownload />
                  {t('about.resumeDE', language)}
                </motion.a>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        className="scroll-indicator"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
      >
        <motion.div
          className="scroll-mouse"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
        >
          <div className="scroll-wheel" />
        </motion.div>
      </motion.div>
    </section>
  )
}
